import React from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  CircularProgress,
  Snackbar,
  Alert,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
} from "@mui/material";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import { createStudentAbsence, fetchReservaStudentIds } from "../../api/ticketingApi";
import { useClientes } from "../../hooks/useClientes";
import IconoAusenciaAlum from "../../imagenes/Iconos/Iconos_botones/iconoausencia.svg";

type Props = {
  reservaId: number;
  studentId?: number;
  buttonText?: string;
  onSuccess?: (ticketId: string) => void;
  variant?: "text" | "outlined" | "contained";
  color?: "primary" | "secondary" | "inherit" | "success" | "error" | "info" | "warning";
  size?: "small" | "medium" | "large";
};

export default function StudentAbsenceButton({
  reservaId,
  studentId,
  onSuccess,
  buttonText = "",
  variant = "outlined",
  color = "primary",
  size = "small",
}: Props) {
  const [open, setOpen] = React.useState(false);
  const [comment, setComment] = React.useState("");
  const [files, setFiles] = React.useState<File[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [student, setStudent] = React.useState<number | "">("");
  const [studentIds, setStudentIds] = React.useState<number[]>([]);
  const [snack, setSnack] = React.useState<{
    open: boolean;
    msg: string;
    sev: "success" | "error";
  }>({ open: false, msg: "", sev: "success" });

  // Cargamos el catálogo de clientes (alumnado) para mapear id -> nombre
  const { rows: clientes } = useClientes();

  const needSelect = studentId == null;

  React.useEffect(() => {
    if (open && needSelect) {
      fetchReservaStudentIds(reservaId)
        .then((ids) => setStudentIds(ids))
        .catch(() => setStudentIds([]));
    }
  }, [open, needSelect, reservaId]);

  const alumnoLabel = (id?: number) => {
    if (!id) return "";
    const cli = clientes.find((c) => c.id === id);
    return cli?.nombreCompleto || `Alumno #${id}`;
  };

  const onChooseFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) setFiles(Array.from(e.target.files));
  };

  const submit = async () => {
    const sid = needSelect ? Number(student) : Number(studentId);
    if (!sid) {
      setSnack({ open: true, msg: "Selecciona un alumno", sev: "error" });
      return;
    }
    setLoading(true);
    try {
      const res = await createStudentAbsence(reservaId, sid, comment, files);
      const ticketId = res.id || res.ticketId || "";
      setSnack({
        open: true,
        msg: res.dedup ? `Adjuntado al ticket ${ticketId}` : `Ticket creado: ${ticketId}`,
        sev: "success",
      });
      onSuccess?.(ticketId);
      setOpen(false);
      setComment("");
      setFiles([]);
      setStudent("");
    } catch (e: any) {
      setSnack({
        open: true,
        msg: e?.response?.data?.message || "Error creando el ticket",
        sev: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const selectedName = !needSelect ? alumnoLabel(studentId) : "";

  return (
    <>
      <Button
        startIcon={
          <img
            src={IconoAusenciaAlum}
            alt="Ausencia alumno"
            style={{ width: 20, height: 20 }}
          />
        }
        variant={variant}
        color={color}
        size={size}
        onClick={() => setOpen(true)}
        title="Ausencia de alumno" 
        sx={{
          minHeight: "28px",
          height: "100%", 
          minWidth: "auto", 
          px: 1.25, 
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          "& .MuiButton-startIcon": { mr: -0.1 }, 
          mr: 0,
        }}
      >
        {buttonText}
      </Button>

      <Dialog open={open} onClose={() => !loading && setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {`Reportar ausencia del/a alumno/a${selectedName ? ` — ${selectedName}` : ""}`}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {needSelect ? (
              <FormControl fullWidth>
                <InputLabel id="student-abs-label">Alumno/a</InputLabel>
                <Select
                  labelId="student-abs-label"
                  label="Alumno"
                  value={student}
                  onChange={(e) => setStudent(e.target.value as number)}
                >
                  {studentIds.map((id) => (
                    <MenuItem key={id} value={id}>
                      {alumnoLabel(id)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <TextField
                label="Alumno"
                value={alumnoLabel(studentId)}
                InputProps={{ readOnly: true }}
                fullWidth
              />
            )}
            <TextField
              label="Comentario (opcional)"
              multiline
              minRows={3}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              fullWidth
            />
            <Button component="label" variant="text">
              Adjuntar archivos
              <input hidden multiple type="file" onChange={onChooseFiles} />
            </Button>
            {files.length > 0 && <small>{files.length} archivo(s) seleccionados</small>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={loading}>
            Cancelar
          </Button>
          <Button onClick={submit} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : "Crear ticket"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snack.sev} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
}
